"use client";

import { FiZoomIn, FiZoomOut } from "react-icons/fi";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

const MAX_ZOOM = 8;

interface EpaperClipZoomViewerProps {
  src: string;
  alt?: string;
  className?: string;
  /** CSS height for the whole viewer (image area + toolbar). */
  viewportHeight?: string;
  showControls?: boolean;
}

export default function EpaperClipZoomViewer({
  src,
  alt = "",
  className = "",
  viewportHeight = "min(70vh, 800px)",
  showControls = true,
}: EpaperClipZoomViewerProps) {
  if (!src) {
    return (
      <div
        className={`flex items-center justify-center bg-[#f0f0f0] dark:bg-[#212121] ${className}`}
        style={{ height: viewportHeight }}
      >
        <span className="text-sm text-gray-500 dark:text-gray-400">No image available</span>
      </div>
    );
  }

  return (
    <div
      className={`flex w-full flex-col overflow-hidden bg-[#f0f0f0] dark:bg-[#212121] ${className}`}
      style={{ height: viewportHeight }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={MAX_ZOOM}
        centerOnInit
        limitToBounds={false}
        doubleClick={{ disabled: false, mode: "toggle", step: 2 }}
        wheel={{ step: 0.12, smoothStep: 0.004 }}
        pinch={{ disabled: false }}
        panning={{ disabled: false }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <div className="flex h-full min-h-0 flex-col">
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <TransformComponent
                wrapperClass="!w-full !h-full"
                contentClass="!w-full !h-full flex items-center justify-center p-4"
              >
                <img
                  src={src}
                  alt={alt}
                  className="h-auto w-auto max-h-full max-w-full select-none"
                  draggable={false}
                />
              </TransformComponent>
            </div>

            {showControls ? (
              <div className="flex shrink-0 flex-col items-center gap-1 border-t border-[#ddd] bg-[#ececec] px-3 py-2 dark:border-[#444] dark:bg-[#2d2d2d]">
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-md bg-[#1a1a1a]/85 text-xl text-white shadow-sm dark:bg-white/15"
                    onClick={() => zoomOut()}
                    aria-label="Zoom out"
                  >
                    <FiZoomOut />
                  </button>
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-md bg-[#1a1a1a]/85 text-xl text-white shadow-sm dark:bg-white/15"
                    onClick={() => zoomIn()}
                    aria-label="Zoom in"
                  >
                    <FiZoomIn />
                  </button>
                  <button
                    type="button"
                    className="flex h-10 min-w-[3rem] items-center justify-center rounded-md bg-[#1a1a1a]/85 px-3 text-xs font-semibold text-white shadow-sm dark:bg-white/15"
                    onClick={() => resetTransform()}
                    aria-label="Reset zoom"
                  >
                    Reset
                  </button>
                </div>
                <p className="text-[10px] text-[#666] dark:text-[#aaa] sm:text-xs">
                  Pinch, scroll, or double-tap the image to zoom
                </p>
              </div>
            ) : null}
          </div>
        )}
      </TransformWrapper>
    </div>
  );
}
